import type { Node as ProseNode } from 'prosemirror-model';
import type { Node } from 'prosemirror-model';
import { EditorState } from 'prosemirror-state';
import type { EditorView as EditorViewType } from 'prosemirror-view';
import { EditorView } from 'prosemirror-view';
import { baseKeymap, chainCommands, exitCode } from 'prosemirror-commands';
import { keymap } from 'prosemirror-keymap';
import { history, redo, undo } from 'prosemirror-history';
import { splitListItem } from 'prosemirror-schema-list';
import { columnResizing, tableEditing, TableView } from 'prosemirror-tables';
import { manualSchema, XAMIX_ELEMENT_ROOT } from './schema';
import { xamixInlineNodeView, blockXamixNodeView, inlineImageNodeView, inlineSvgNodeView } from './xamix-node-view';

/** Table view that adds column resize handles and preserves our table attrs (margin, colors, class). */
class ResizableTableView extends TableView {
  constructor(node: Node, defaultCellMinWidth: number, _editorView?: EditorViewType) {
    super(node, defaultCellMinWidth);
    this.applyTableAttrs();
  }

  update(node: Node): boolean {
    const result = super.update(node);
    if (result) this.applyTableAttrs();
    return result;
  }

  private applyTableAttrs(): void {
    const n = this.node;
    const margin = (n.attrs as { margin?: string }).margin ?? '';
    const bg = (n.attrs as { backgroundColor?: string }).backgroundColor ?? '';
    const borderColor = (n.attrs as { borderColor?: string }).borderColor ?? '';
    let colWidths = (n.attrs as { colWidths?: number[] | null }).colWidths;
    
    // DEBUG: Log what's happening
    console.log('[DEBUG applyTableAttrs] Node colWidths:', colWidths);
    
    // If colWidths not set on table node, try to read from cell data-colwidth attributes
    if (!colWidths || !Array.isArray(colWidths) || colWidths.length === 0) {
      const firstRow = n.firstChild;
      if (firstRow) {
        const cellWidths: number[] = [];
        for (let i = 0; i < firstRow.childCount; i++) {
          const cell = firstRow.child(i);
          const colwidth = cell.attrs.colwidth as number[] | null | undefined;
          console.log('[DEBUG applyTableAttrs] Cell', i, 'colwidth attr:', colwidth);
          if (colwidth && colwidth[0] != null && colwidth[0] > 0) {
            cellWidths.push(colwidth[0]);
          } else {
            // If any cell doesn't have a width, bail out and use default
            cellWidths.length = 0;
            break;
          }
        }
        if (cellWidths.length > 0) {
          colWidths = cellWidths;
          console.log('[DEBUG applyTableAttrs] Using cellWidths:', colWidths);
        }
      }
    }
    
    // Also try reading from DOM if still no widths (handles case where DOM was updated but node wasn't)
    if (!colWidths || !Array.isArray(colWidths) || colWidths.length === 0) {
      const domColgroup = this.table.querySelector('colgroup');
      console.log('[DEBUG applyTableAttrs] Looking for DOM colgroup:', domColgroup ? 'found' : 'not found');
      if (domColgroup) {
        const domCols = domColgroup.querySelectorAll('col');
        console.log('[DEBUG applyTableAttrs] DOM cols count:', domCols.length);
        if (domCols.length > 0) {
          const domWidths: number[] = [];
          for (let i = 0; i < domCols.length; i++) {
            const styleWidth = domCols[i].style.width;
            console.log('[DEBUG applyTableAttrs] DOM col', i, 'width:', styleWidth);
            if (styleWidth && styleWidth.endsWith('px')) {
              domWidths.push(parseInt(styleWidth, 10));
            } else {
              break;
            }
          }
          if (domWidths.length === domCols.length && domWidths.length > 0) {
            colWidths = domWidths;
            console.log('[DEBUG applyTableAttrs] Using domWidths:', colWidths);
          }
        }
      }
    }
    
    console.log('[DEBUG applyTableAttrs] Final colWidths:', colWidths);
    
    this.table.className = 'manual-table';
    let style = 'border-collapse: collapse; border: 1px solid ' + (borderColor || '#cac4d0') + '; table-layout: fixed;';
    if (margin) style += '; margin: ' + margin;
    if (bg) style += '; background-color: ' + bg;
    if (borderColor) style += '; --table-border-color: ' + borderColor;
    this.table.setAttribute('style', style);
    if (borderColor) this.table.setAttribute('data-table-border-color', borderColor);
    if (margin) this.table.setAttribute('data-margin', margin);
    if (bg) this.table.setAttribute('data-table-background', bg);
    
    // Generate colgroup with column widths
    // Priority: 1) Existing DOM col widths (preserves drag updates), 2) Node colWidths, 3) Cell colwidth attrs
    let colgroup = this.table.querySelector('colgroup');
    const existingCols = colgroup ? Array.from(colgroup.querySelectorAll('col')) : [];
    console.log('[DEBUG applyTableAttrs] Existing cols in DOM:', existingCols.length);
    
    // First, try to get widths from existing DOM (this preserves drag updates)
    let domWidths: number[] = [];
    if (existingCols.length > 0) {
      for (const col of existingCols) {
        const styleWidth = col.style.width;
        console.log('[DEBUG applyTableAttrs] DOM col width:', styleWidth);
        if (styleWidth && styleWidth.endsWith('px')) {
          domWidths.push(parseInt(styleWidth, 10));
        } else {
          // If any col doesn't have a valid width, stop
          break;
        }
      }
    }
    
    // If we got valid widths from DOM, use those
    if (domWidths.length === existingCols.length && domWidths.length > 0) {
      colWidths = domWidths;
      console.log('[DEBUG applyTableAttrs] Using DOM widths:', colWidths);
    } else if (!colWidths || !Array.isArray(colWidths) || colWidths.length === 0) {
      // If no valid DOM widths and no node colWidths, try cell attrs
      console.log('[DEBUG applyTableAttrs] Trying cell attrs since no DOM or node widths');
      const firstRow = n.firstChild;
      if (firstRow) {
        const cellWidths: number[] = [];
        for (let i = 0; i < firstRow.childCount; i++) {
          const cell = firstRow.child(i);
          const colwidth = cell.attrs.colwidth as number[] | null | undefined;
          if (colwidth && colwidth[0] != null && colwidth[0] > 0) {
            cellWidths.push(colwidth[0]);
          } else {
            cellWidths.length = 0;
            break;
          }
        }
        if (cellWidths.length > 0) {
          colWidths = cellWidths;
        }
      }
    }
    
    console.log('[DEBUG applyTableAttrs] Final colWidths to apply:', colWidths);
    
    if (!colgroup) {
      colgroup = document.createElement('colgroup');
      const firstChild = this.table.firstChild;
      if (firstChild) {
        this.table.insertBefore(colgroup, firstChild);
      } else {
        this.table.appendChild(colgroup);
      }
    }
    
    // Always preserve existing DOM elements - just update their widths
    if (existingCols.length > 0 && colWidths && Array.isArray(colWidths)) {
      console.log('[DEBUG applyTableAttrs] Updating existing cols with widths:', colWidths);
      // Update existing col elements with current widths
      for (let i = 0; i < existingCols.length; i++) {
        const col = existingCols[i];
        if (i < colWidths.length && colWidths[i] != null && colWidths[i] > 0) {
          col.style.width = colWidths[i] + 'px';
          console.log('[DEBUG applyTableAttrs] Set col', i, 'width to:', colWidths[i] + 'px');
        } else {
          col.style.width = '';
        }
        // Ensure col is in colgroup
        if (col.parentNode !== colgroup) {
          colgroup.appendChild(col);
        }
      }
    } else if (colWidths && Array.isArray(colWidths) && colWidths.length > 0) {
      // No existing cols, create new ones
      console.log('[DEBUG applyTableAttrs] Creating new cols with widths:', colWidths);
      colgroup.innerHTML = '';
      for (const w of colWidths) {
        const col = document.createElement('col');
        if (w != null && w > 0) {
          col.style.width = w + 'px';
        }
        colgroup.appendChild(col);
      }
    } else {
      // Default to 3 columns
      colgroup.innerHTML = '';
      for (let i = 0; i < 3; i++) {
        const col = document.createElement('col');
        colgroup.appendChild(col);
      }
    }
  }
}

const splitListItemCmd = splitListItem(manualSchema.nodes.list_item);

const insertHardBreak: typeof exitCode = (state, dispatch) => {
  if (dispatch) {
    dispatch(state.tr.replaceSelectionWith(manualSchema.nodes.hard_break.create()).scrollIntoView());
  }
  return true;
};

const plugins = [
  history(),
  keymap({ 'Mod-z': undo, 'Mod-y': redo, 'Mod-Shift-z': redo }),
  keymap({ Enter: splitListItemCmd }),
  keymap({ 'Shift-Enter': chainCommands(exitCode, insertHardBreak) }),
  keymap(baseKeymap),
  columnResizing({ View: ResizableTableView, defaultCellMinWidth: 80, cellMinWidth: 40 }),
  tableEditing(),
];

export interface EditorOptions {
  element: HTMLElement;
  initialState?: EditorState;
  initialDoc?: ProseNode;
  onUpdate?: (state: EditorState) => void;
}

export function createEditor(options: EditorOptions): EditorView {
  const emptyDoc = manualSchema.topNodeType.create(null, [
    manualSchema.nodes.paragraph.create(null),
  ]);

  let state = options.initialState;
  if (!state) {
    const doc = options.initialDoc ?? emptyDoc;
    state = EditorState.create({ doc, plugins });
  }

  const view = new EditorView(options.element, {
    state,
    dispatchTransaction(tr) {
      const newState = view.state.apply(tr);
      view.updateState(newState);
      options.onUpdate?.(newState);
    },
    nodeViews: {
      xamix: (node, view, getPos) => xamixInlineNodeView(node, view, getPos, XAMIX_ELEMENT_ROOT),
      block_xamix: (node, view, getPos) => blockXamixNodeView(node, view, getPos, XAMIX_ELEMENT_ROOT),
      inline_image: (node, view, getPos) => inlineImageNodeView(node, view, getPos),
      inline_svg: (node, view, getPos) => inlineSvgNodeView(node, view, getPos),
    },
  });

  return view;
}

export type { Schema };
